import {noop} from '@github-ui/noop'
import {testIdProps} from '@github-ui/test-id-props'
import {ActionList, ActionMenu} from '@primer/react'
import {memo, useCallback, useId, useMemo} from 'react'
import {flushSync} from 'react-dom'

import {FieldSortAsc, FieldSortDesc} from '../api/stats/contracts'
import {onSubMenuMultiSelection, sortColumnsDeterministically} from '../helpers/util'
import {usePostStats} from '../hooks/common/use-post-stats'
import {useMemexProjectViewRootHeight} from '../hooks/use-memex-app-root-height'
import {useSortedBy} from '../hooks/use-sorted-by'
import {useViewOptionsStatsUiKey} from '../hooks/use-view-options-stats-ui-key'
import {isSortableColumn} from '../models/column-capabilities'
import {useAllColumns} from '../state-providers/columns/use-all-columns'
import {getColumnIcon} from './column-detail-helpers'
import {ColumnSortIcon} from './common/column-sort-icon'
import styles from './sort-by-menu.module.css'
import {describeSortRanking, nextSortDirection} from './sorted-by'

type MenuProps = {
  open: boolean
  setOpen: (open: boolean) => void
  anchorRef: React.RefObject<HTMLElement | null>
  id?: string
}

export const SortByMenu = memo<MenuProps>(function SortByMenuComponent({id: externalId, open, setOpen, anchorRef}) {
  const fallbackId = useId()
  const id = externalId ?? fallbackId

  const {clientHeight} = useMemexProjectViewRootHeight({
    onResize: () => {
      if (open) {
        // eslint-disable-next-line @eslint-react/dom/no-flush-sync
        flushSync(() => {
          setOpen(false)
        })
        setOpen(true)
      }
    },
  })

  const labelId = `${id}_label`
  const descriptionId = `${id}_description`

  return (
    <ActionMenu open={open} anchorRef={anchorRef} onOpenChange={noop}>
      <ActionMenu.Overlay
        style={{
          maxHeight: clientHeight,
        }}
        {...testIdProps('sort-by-menu')}
        role="dialog"
        aria-label="Select a field to sort by"
        onEscape={() => setOpen(false)}
        onClickOutside={() => setOpen(false)}
        className={styles.ActionMenu_Overlay}
      >
        <div id={labelId} className={styles.Box}>
          Sort by
        </div>
        <div id={descriptionId} className={styles.Box_1}>
          Select up to 2 fields
        </div>
        <Options id={id} key={String(open)} setOpen={setOpen} labelledBy={labelId} describedBy={descriptionId} />
      </ActionMenu.Overlay>
    </ActionMenu>
  )
})

const Options = memo(function OptionsComponent({
  setOpen,
  id,
  labelledBy,
  describedBy,
}: Pick<MenuProps, 'setOpen' | 'id'> & {
  labelledBy: string
  describedBy?: string
}) {
  const {allColumns} = useAllColumns()
  const {sorts, isSorted, clearSortedBy, setColumnSort} = useSortedBy()
  const {postStats} = usePostStats()
  const statsUiKey = useViewOptionsStatsUiKey()

  const columnItems = useMemo(() => {
    const baseColumnItems = allColumns
      .slice()
      .sort(sortColumnsDeterministically)
      .map(column => {
        const sortIndex = sorts.findIndex(sort => sort.column.id === column.id)
        const columnSort = sorts[sortIndex]

        const onSelection = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
          const direction = nextSortDirection(columnSort?.direction ?? null)
          setColumnSort(column, direction)

          postStats({
            name: direction === 'desc' ? FieldSortDesc : FieldSortAsc,
            ui: statsUiKey,
            context: column.id,
          })

          e.stopPropagation()
        }

        const Icon = getColumnIcon(column.dataType)

        const showRanking = sorts.length > 1

        return (
          <ActionList.Item
            key={column.id}
            onSelect={e => {
              onSubMenuMultiSelection(e)
              onSelection(e)
            }}
            selected={columnSort !== undefined}
            {...testIdProps(`sort-by-${column.name}`)}
            disabled={!isSortableColumn(column.dataType)}
          >
            <ActionList.LeadingVisual>
              <Icon />
            </ActionList.LeadingVisual>
            {column.name}

            {/* The conditional has to be duplicated because if we combine it, we'd have to render the items in a
            fragment (<></>) which would break slots (ActionList.TrailingVisual must be a direct child of
            ActionList.Item) And we can't just put the hidden text inside the trailing visual either because it would
            be hidden from screen readers. */}
            {columnSort !== undefined && (
              <span className="sr-only">
                ({showRanking ? `${describeSortRanking(sortIndex)} ` : ''}sort:{' '}
                {columnSort.direction === 'asc' ? 'ascending' : 'descending'})
              </span>
            )}
            {columnSort !== undefined && (
              <ActionList.TrailingVisual>
                <ColumnSortIcon direction={columnSort.direction} index={showRanking ? sortIndex : undefined} />
              </ActionList.TrailingVisual>
            )}
          </ActionList.Item>
        )
      })

    return baseColumnItems
  }, [allColumns, sorts, setColumnSort, postStats, statsUiKey])

  const handleClearSort = useCallback(() => {
    clearSortedBy()
    setOpen(false)
  }, [clearSortedBy, setOpen])

  return (
    <ActionList id={id} selectionVariant="single" aria-labelledby={labelledBy} aria-describedby={describedBy}>
      {columnItems}
      <ActionList.Divider />
      <ActionList.Item
        selected={!isSorted}
        key={'sort-by-none'}
        onSelect={handleClearSort}
        {...testIdProps('sort-by-none')}
      >
        No sorting
      </ActionList.Item>
    </ActionList>
  )
})
